/**
 * CringeOMeter — horizontal gauge rendering a 1..10 cringe score.
 *
 * Layout:
 * - A navy track holds the orange fill whose width is `(score/10)*100%`.
 * - A hot-pink needle is positioned at the same proportional offset.
 * - A label below shows the numeric score and the supplied caption.
 *
 * Thematic colours (fill, needle, label colour via host text) live in
 * `brand.css` as `.cringe-meter-fill` and `.cringe-meter-needle`; structural
 * layout (track height, needle positioning) is set inline here because
 * it's purely positional, not brand-coded.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Component
 * Responsibilities" (CringeOMeter) and Requirement 4.4.
 */

/**
 * Build the Cringe-O-Meter DOM. Returns a detached element the caller
 * can append wherever it likes.
 *
 * The `score` is clamped to the valid 1..10 range defensively; valid
 * server-side validation already enforces this (see `RoastResult`), but
 * clamping keeps a surprising input from producing an off-canvas needle.
 */
export function renderCringeOMeter(score: number, label: string): HTMLElement {
  const clamped = Math.max(1, Math.min(10, Math.round(score)));
  const pct = (clamped / 10) * 100;

  const root = document.createElement('div');
  root.className = 'cringe-o-meter';
  root.setAttribute('role', 'meter');
  root.setAttribute('aria-valuenow', String(clamped));
  root.setAttribute('aria-valuemin', '1');
  root.setAttribute('aria-valuemax', '10');
  root.setAttribute('aria-label', `Cringe-o-meter: ${label}`);

  const track = document.createElement('div');
  track.className = 'cringe-meter-track';
  // Structural (non-thematic) layout: the track is a relatively-positioned
  // 24 px bar with rounded ends. Colours for fill/needle come from classes
  // in brand.css so they stay in the palette.
  track.style.position = 'relative';
  track.style.height = '24px';
  track.style.background = '#152a4a';
  track.style.borderRadius = '12px';
  track.style.overflow = 'hidden';
  track.style.marginBottom = '8px';

  const fill = document.createElement('div');
  fill.className = 'cringe-meter-fill';
  fill.style.height = '100%';
  fill.style.width = `${pct}%`;
  fill.style.borderRadius = '12px';

  const needle = document.createElement('div');
  needle.className = 'cringe-meter-needle';
  needle.style.position = 'absolute';
  needle.style.top = '-4px';
  needle.style.left = `${pct}%`;
  needle.style.width = '4px';
  needle.style.height = '32px';
  needle.style.borderRadius = '2px';
  needle.style.transform = 'translateX(-2px)';

  track.appendChild(fill);
  track.appendChild(needle);

  const caption = document.createElement('div');
  caption.className = 'cringe-meter-label';

  const scoreSpan = document.createElement('span');
  scoreSpan.className = 'cringe-meter-score';
  scoreSpan.textContent = String(clamped);

  const captionSpan = document.createElement('span');
  captionSpan.className = 'cringe-meter-caption';
  captionSpan.textContent = label;

  caption.appendChild(scoreSpan);
  caption.appendChild(document.createTextNode('/10 \u2014 '));
  caption.appendChild(captionSpan);

  root.appendChild(track);
  root.appendChild(caption);

  return root;
}
